import React from 'react';
import { timezoneToCountryCode } from '../../../utils/timezoneToCountry';
import CountryFlag from '../../common/CountryFlag';
import { Clock, X, ArrowRight, CalendarDays, User, Tag, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import CircularProgress from '../../common/CircularProgress';
import { TimeSlotPicker } from './TimeSlotPicker';
import { gridCellsForSession, GRID_CELL_MINUTES, type StartTimeOption } from './bookingUtils';
import { addMinutesToTime, timeToMinutes } from './utils';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  confirming: boolean;
  sessionInfo: {
    patient: string;
    currentDate: string;
    currentTime: string;
    currentEndTime: string;
    duration: number;
    therapyType?: string;
  };
  newDate: string;
  newTime: string;
  newEndTime: string;
  therapistTimezone: string;
  patientTimezone?: string | null;
  patientCurrentTime?: string | null;
  patientCurrentEndTime?: string | null;
  patientNewTime?: string | null;
  patientNewEndTime?: string | null;
  patientCountryCode?: string | null;
  /** Inicios en marcas de 5 sobre el slot elegido. Sin esto, el modal se comporta como antes. */
  startTimeOptions?: StartTimeOption[];
  onSelectStartTime?: (startTime: string) => void;
};

const formatDdMmYyyy = (s: string): string => {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
};

const RescheduleConfirmModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  confirming,
  sessionInfo,
  newDate,
  newTime,
  newEndTime,
  therapistTimezone,
  patientTimezone,
  patientCurrentTime,
  patientCurrentEndTime,
  patientNewTime,
  patientNewEndTime,
  startTimeOptions,
  onSelectStartTime,
}) => {
  // Mismo aviso que al agendar: correr el inicio ocupa la celda anterior y la siguiente enteras.
  const selectedOption = startTimeOptions?.find(o => o.startTime === newTime);
  const gridFootprint = (() => {
    if (!selectedOption || selectedOption.offsetMin === 0) return null;
    const cells = gridCellsForSession(timeToMinutes(newTime), sessionInfo.duration);
    if (cells.length === 0) return null;
    return {
      from: addMinutesToTime('00:00', cells[0]),
      to: addMinutesToTime('00:00', cells[cells.length - 1] + GRID_CELL_MINUTES),
    };
  })();

  if (!open) return null;

  const therapistIso2 = timezoneToCountryCode(therapistTimezone);

  const patientIso2 = patientTimezone ? timezoneToCountryCode(patientTimezone) : null;
  const patientCity = patientTimezone
    ? (patientTimezone.split('/').pop()?.replace(/_/g, ' ') ?? '')
    : '';

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
      }}
      onClick={() => { if (!confirming) onClose(); }}
    >
      <div
        id="reschedule-confirm-modal-content"
        style={{
          background: '#fff',
          borderRadius: '16px',
          width: '640px',
          maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {confirming ? (
          <div style={{ padding: '3rem', display: 'grid', placeItems: 'center' }}>
            <CircularProgress sizeRem={2.5} label="Reprogramando sesión" />
          </div>
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-foreground m-0">Reprogramar sesión</h2>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Patient */}
            <div className="flex items-center gap-2 mb-4 text-sm text-muted-foreground">
              <User className="w-4 h-4" />
              <span className="font-medium text-foreground">{sessionInfo.patient}</span>
              <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-primary/30 text-xs font-medium text-primary">
                <Clock className="w-3 h-3" />
                {sessionInfo.duration} min
              </span>
            </div>

            {/* Current → New */}
            <div className="flex items-stretch gap-3 mb-5">
              {/* Current */}
              <div className="flex-1 p-3 rounded-xl border border-border bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1.5">Sesión actual</p>
                <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
                  {formatDdMmYyyy(sessionInfo.currentDate)}
                </div>
                <div
                  data-testid="reschedule-current-time"
                  className="flex items-center gap-1.5 text-sm font-medium text-foreground mt-1"
                >
                  <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                  <span>{sessionInfo.currentTime} - {sessionInfo.currentEndTime}</span>
                  {therapistIso2 && <CountryFlag iso2={therapistIso2} size="0.875rem" className="ml-0.5" />}
                </div>
                {patientTimezone && patientCurrentTime && patientCurrentEndTime && (
                  <div
                    data-testid="reschedule-current-patient-time"
                    className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-muted-foreground mt-1.5"
                  >
                    <User className="w-3 h-3 flex-shrink-0" />
                    <span className="whitespace-nowrap">{patientCurrentTime} – {patientCurrentEndTime}</span>
                    {(patientCity || patientIso2) && (
                      <span data-testid="reschedule-current-patient-location" className="whitespace-nowrap">
                        {patientCity && `· ${patientCity}`}{patientIso2 && <> <CountryFlag iso2={patientIso2} size="0.75rem" /></>}
                      </span>
                    )}
                    {patientCity && (
                      <Tooltip delayDuration={150}>
                        <TooltipTrigger asChild>
                          <span
                            data-testid="reschedule-current-patient-tz-info-btn"
                            className="inline-flex items-center flex-shrink-0 cursor-default text-primary"
                            aria-label="Sobre el huso horario del paciente"
                          >
                            <Info size={12} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={6} className="max-w-[260px] text-xs leading-relaxed">
                          Tené en cuenta que &quot;{patientCity}&quot; no necesariamente es donde vive el paciente, sino el huso horario que tiene configurado en su perfil.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )}
                {sessionInfo.therapyType && (
                  <div
                    data-testid="reschedule-current-therapy-type"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5"
                  >
                    <Tag className="w-3 h-3 flex-shrink-0" />
                    <span>{sessionInfo.therapyType}</span>
                  </div>
                )}
              </div>

              {/* Arrow */}
              <div className="flex items-center">
                <ArrowRight className="w-5 h-5 text-primary" />
              </div>

              {/* New */}
              <div className="flex-1 p-3 rounded-xl border-2 border-primary/40 bg-primary/5">
                <p className="text-xs text-primary font-medium mb-1.5">Nueva sesión</p>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                  <CalendarDays className="w-3.5 h-3.5 text-primary" />
                  {formatDdMmYyyy(newDate)}
                </div>
                <div
                  data-testid="reschedule-new-time"
                  className="flex items-center gap-1.5 text-sm font-semibold text-foreground mt-1"
                >
                  <Clock className="w-3.5 h-3.5 text-primary" />
                  <span>{newTime} - {newEndTime}</span>
                  {therapistIso2 && <CountryFlag iso2={therapistIso2} size="0.875rem" className="ml-0.5" />}
                </div>
                {startTimeOptions && startTimeOptions.length > 1 && onSelectStartTime && (
                  <div data-testid="reschedule-start-time-options" className="mt-3">
                    <TimeSlotPicker
                      variant="chips"
                      label="Horario de inicio"
                      slots={startTimeOptions.map(o => ({ time: o.startTime, available: o.enabled }))}
                      selectedTime={newTime}
                      onSelect={onSelectStartTime}
                    />
                    {gridFootprint && (
                      <p data-testid="reschedule-grid-footprint" className="text-xs text-muted-foreground mt-2 m-0">
                        Al correr el inicio, tu agenda queda ocupada de {gridFootprint.from} a {gridFootprint.to}.
                      </p>
                    )}
                  </div>
                )}
                {patientTimezone && patientNewTime && patientNewEndTime && (
                  <div
                    data-testid="reschedule-new-patient-time"
                    className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-muted-foreground mt-1.5"
                  >
                    <User className="w-3 h-3 flex-shrink-0" />
                    <span className="whitespace-nowrap">{patientNewTime} – {patientNewEndTime}</span>
                    {(patientCity || patientIso2) && (
                      <span data-testid="reschedule-new-patient-location" className="whitespace-nowrap">
                        {patientCity && `· ${patientCity}`}{patientIso2 && <> <CountryFlag iso2={patientIso2} size="0.75rem" /></>}
                      </span>
                    )}
                    {patientCity && (
                      <Tooltip delayDuration={150}>
                        <TooltipTrigger asChild>
                          <span
                            data-testid="reschedule-new-patient-tz-info-btn"
                            className="inline-flex items-center flex-shrink-0 cursor-default text-primary"
                            aria-label="Sobre el huso horario del paciente"
                          >
                            <Info size={12} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={6} className="max-w-[260px] text-xs leading-relaxed">
                          Tené en cuenta que &quot;{patientCity}&quot; no necesariamente es donde vive el paciente, sino el huso horario que tiene configurado en su perfil.
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )}
                {sessionInfo.therapyType && (
                  <div
                    data-testid="reschedule-new-therapy-type"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5"
                  >
                    <Tag className="w-3 h-3 flex-shrink-0 text-primary" />
                    <span>{sessionInfo.therapyType}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button onClick={onConfirm}>
                Continuar
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RescheduleConfirmModal;
